/* Supplier directory (wholesalers): CRUD, invitations, EAN linking,
   per-field supplier updates and PDF e-mail orders. */

import { api } from "./api";

export type SupplierStatus = "not_linked" | "invited" | "declined" | "registered" | "connected";

export type SupplierRecord = {
  id: number;
  name: string;
  registration_number: string;
  country: string;
  email: string;
  contact_name: string;
  phone: string;
  notes: string;
  language: string;
  status: SupplierStatus;
  counterparty_slug: string | null;
  counterparty_name: string | null;
  product_count: number;
  unlinked_ean_count: number;
  /** This company is known to us only because we typed its name onto our own
   *  product cards — it is not registered here. The list tints those rows and
   *  still offers «send a request». */
  from_own_cards: boolean;
  created_at: string;
};

export type SupplierRecordIn = {
  name: string;
  registration_number?: string;
  country?: string;
  email?: string;
  contact_name?: string;
  phone?: string;
  notes?: string;
  language?: string;
};

export type MatchProposal = {
  product_id: number;
  product_name: string;
  ean: string;
  canonical_id: number;
  canonical_name: string;
  canonical_is_active: boolean;
};

export type UpdateChange = {
  field: string;
  mine: string | number | null;
  supplier_value: string | number | null;
  previous_value: string | number | null;
};

export type ProductUpdates = {
  product_id: number;
  product_name: string;
  supplier_record_id: number | null;
  canonical_is_active: boolean;
  changes: UpdateChange[];
};

export type EmailOrder = {
  id: number;
  reference: string;
  sent_to: string;
  note: string;
  status: "sent" | "failed";
  sent_at: string | null;
  items: { product_id: number; name: string; sku: string; ean: string; unit: string; qty: number }[];
};

const base = (slug: string) => `/t/${slug}/supplier-directory`;

export const fetchSupplierDirectory = (slug: string, q = ""): Promise<SupplierRecord[]> =>
  api.get<SupplierRecord[]>(`${base(slug)}${q ? `?q=${encodeURIComponent(q)}` : ""}`);

export const createSupplierRecord = (slug: string, data: SupplierRecordIn): Promise<SupplierRecord> =>
  api.post<SupplierRecord>(base(slug), data);

export const updateSupplierRecord = (
  slug: string,
  id: number,
  data: Partial<SupplierRecordIn>,
): Promise<SupplierRecord> => api.patch<SupplierRecord>(`${base(slug)}/${id}`, data);

export const deleteSupplierRecord = (slug: string, id: number): Promise<void> =>
  api.delete(`${base(slug)}/${id}`);

export const inviteSupplier = (slug: string, id: number, message = ""): Promise<SupplierRecord> =>
  api.post<SupplierRecord>(`${base(slug)}/${id}/invite`, { message });

export const fetchMatchProposals = (slug: string, id: number): Promise<MatchProposal[]> =>
  api.get<MatchProposal[]>(`${base(slug)}/${id}/match-proposals`);

export const linkSupplierProducts = (
  slug: string,
  id: number,
  pairs: { product_id: number; canonical_id: number }[],
): Promise<{ linked: number }> =>
  api.post<{ linked: number }>(`${base(slug)}/${id}/link-products`, { pairs });

export const fetchPendingSupplierUpdates = (slug: string): Promise<ProductUpdates[]> =>
  api.get<ProductUpdates[]>(`${base(slug)}/updates/pending`);

export const resolveSupplierUpdate = (
  slug: string,
  productId: number,
  field: string,
  action: "accept" | "reject",
): Promise<void> =>
  api.post(`${base(slug)}/updates/resolve`, { product_id: productId, field, action });

/** «Принять» / «Отклонить» on the strip under a product row: every field. */
export const resolveAllSupplierUpdates = (
  slug: string,
  productId: number,
  action: "accept" | "reject",
): Promise<void> =>
  api.post(`${base(slug)}/updates/resolve-all`, { product_id: productId, action });

// ── New positions from suppliers (spec 20260915 §1.1) ──────────────────────

export type SupplierOffer = {
  product_id: number;
  name: string;
  ean: string;
  sku: string;
  image_url: string | null;
  thumb_url?: string | null;
  link_id: number;
  supplier_name: string;
  supplier_slug: string;
  seller_category_id: number | null;
  group_name: string | null;
  subgroup_name: string | null;
  cost_price: number | null;
  /** A category choice remembered from an earlier import. */
  mapped: boolean;
  /** null with mapped=true is «как у поставщика». */
  buyer_category_id: number | null;
};

export const fetchSupplierOffers = (slug: string): Promise<SupplierOffer[]> =>
  api.get<SupplierOffer[]>(`/t/${slug}/supplier-offers`);

export const importSupplierOffers = (
  slug: string,
  productIds: number[],
  mappings: { seller_category_id: number; buyer_category_id: number | null }[],
): Promise<{ created: number }> =>
  api.post<{ created: number }>(`/t/${slug}/supplier-offers/import`, {
    product_ids: productIds,
    mappings,
  });

export const dismissSupplierOffers = (slug: string, productIds: number[]): Promise<void> =>
  api.post(`/t/${slug}/supplier-offers/dismiss`, { product_ids: productIds });

// ── «Недоступна у поставщика» (spec 20260915 §2) ────────────────────────────

export type SupplierGapReason = "" | "access_closed" | "delisted" | "link_ended";

export type SupplierGap = {
  product_id: number;
  name: string;
  supplier_name: string;
  reason: SupplierGapReason;
  since: string | null;
  decided_at: string | null;
  back_at: string | null;
  is_active: boolean;
  public_visible: boolean;
  stock: number;
  open_clients: number;
  open_to_shops: boolean;
  close_when_sold_out: boolean;
  actions: { public?: boolean; clients?: number[]; shops?: boolean; archive?: boolean; sell_off?: boolean };
};

export const fetchSupplierGaps = (slug: string, ids?: number[]): Promise<SupplierGap[]> =>
  api.get<SupplierGap[]>(`/t/${slug}/supplier-gaps${ids?.length ? `?ids=${ids.join(",")}` : ""}`);

export type GapDecision = {
  hide_public: boolean;
  close_clients: boolean;
  close_when_sold_out: boolean;
  archive: boolean;
};

export const decideSupplierGaps = (
  slug: string,
  productIds: number[],
  decision: GapDecision,
): Promise<{ updated: number }> =>
  api.post<{ updated: number }>(`/t/${slug}/supplier-gaps/decide`, {
    product_ids: productIds,
    ...decision,
  });

export const resolveSupplierBack = (
  slug: string,
  productIds: number[],
  restore: boolean,
): Promise<{ updated: number }> =>
  api.post<{ updated: number }>(`/t/${slug}/supplier-gaps/back`, {
    product_ids: productIds,
    restore,
  });

export const sendEmailOrder = (
  slug: string,
  id: number,
  lines: { product_id: number; qty: number }[],
  note = "",
): Promise<EmailOrder> => api.post<EmailOrder>(`${base(slug)}/${id}/email-orders`, { lines, note });

export const fetchEmailOrders = (slug: string, id: number): Promise<EmailOrder[]> =>
  api.get<EmailOrder[]>(`${base(slug)}/${id}/email-orders`);
